import React, { useState, createContext, useContext } from 'react';
import { Bill, BillAllocation } from '../types/bill';
import { bills as seedBills } from '../utils/billSeedData';
interface BillContextType {
  bills: Bill[];
  getBillsByCompanyId: (companyId: string) => Bill[];
  addBill: (bill: Omit<Bill, 'id' | 'created_at' | 'updated_at'>) => void;
  updateBill: (id: string, updates: Partial<Bill>) => void;
  deleteBill: (id: string) => void;
  markBillPaid: (id: string, paymentNotes?: string) => void;
  markBillUnpaid: (id: string) => void;
  addAllocation: (billId: string, allocation: Omit<BillAllocation, 'id'>) => void;
  removeAllocation: (billId: string, allocationId: string) => void;
  updateAllocation: (billId: string, allocationId: string, amount: number) => void;
}
const BillContext = createContext<BillContextType | undefined>(undefined);
export function BillProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [bills, setBills] = useState<Bill[]>(seedBills);
  const getBillsByCompanyId = (companyId: string) => {
    return bills.filter(bill => bill.vendor_company_id === companyId);
  };
  const addBill = (billData: Omit<Bill, 'id' | 'created_at' | 'updated_at'>) => {
    const newBill: Bill = {
      ...billData,
      id: `bill-${Date.now()}`,
      created_at: new Date(),
      updated_at: new Date()
    };
    setBills([newBill, ...bills]);
  };
  const updateBill = (id: string, updates: Partial<Bill>) => {
    setBills(bills.map(bill => bill.id === id ? {
      ...bill,
      ...updates,
      updated_at: new Date()
    } : bill));
  };
  const deleteBill = (id: string) => {
    setBills(bills.filter(bill => bill.id !== id));
  };
  const markBillPaid = (id: string, paymentNotes?: string) => {
    setBills(bills.map(bill => bill.id === id ? {
      ...bill,
      paid: true,
      paid_date: new Date(),
      payment_notes: paymentNotes,
      updated_at: new Date()
    } : bill));
  };
  const markBillUnpaid = (id: string) => {
    setBills(bills.map(bill => bill.id === id ? {
      ...bill,
      paid: false,
      paid_date: undefined,
      payment_notes: undefined,
      updated_at: new Date()
    } : bill));
  };
  const addAllocation = (billId: string, allocationData: Omit<BillAllocation, 'id'>) => {
    setBills(bills.map(bill => {
      if (bill.id === billId) {
        const newAllocation: BillAllocation = {
          ...allocationData,
          id: `alloc-${Date.now()}`
        };
        return {
          ...bill,
          allocations: [...bill.allocations, newAllocation],
          updated_at: new Date()
        };
      }
      return bill;
    }));
  };
  const removeAllocation = (billId: string, allocationId: string) => {
    setBills(bills.map(bill => {
      if (bill.id === billId) {
        return {
          ...bill,
          allocations: bill.allocations.filter(a => a.id !== allocationId),
          updated_at: new Date()
        };
      }
      return bill;
    }));
  };
  const updateAllocation = (billId: string, allocationId: string, amount: number) => {
    setBills(bills.map(bill => {
      if (bill.id === billId) {
        return {
          ...bill,
          allocations: bill.allocations.map(a => a.id === allocationId ? {
            ...a,
            amount
          } : a),
          updated_at: new Date()
        };
      }
      return bill;
    }));
  };
  const value: BillContextType = {
    bills,
    getBillsByCompanyId,
    addBill,
    updateBill,
    deleteBill,
    markBillPaid,
    markBillUnpaid,
    addAllocation,
    removeAllocation,
    updateAllocation
  };
  return <BillContext.Provider value={value} data-id="element-386">{children}</BillContext.Provider>;
}
export function useBills(): BillContextType {
  const context = useContext(BillContext);
  if (!context) {
    throw new Error('useBills must be used within a BillProvider');
  }
  return context;
}